import React from 'react';
import { 
  Cpu, 
  HardDrive, 
  Database, 
  Wifi, 
  Clock, 
  ArrowDownLeft, 
  ArrowUpRight,
  Gauge,
  PowerOff
} from 'lucide-react';

const MetricProgressBar = ({ percentage, color = '#10b981' }) => {
  const safePercent = Math.min(100, Math.max(0, percentage));
  return (
    <div style={{ width: '100%', height: '8px', background: 'rgba(255, 255, 255, 0.08)', borderRadius: '4px', overflow: 'hidden', marginTop: '8px' }}>
      <div 
        style={{ 
          width: `${safePercent}%`, 
          height: '100%', 
          background: color, 
          borderRadius: '4px',
          transition: 'width 0.5s ease-in-out',
          boxShadow: `0 0 8px ${color}`
        }} 
      />
    </div>
  );
};

export const VmVitalsCard = ({ vmVitals }) => {
  const {
    cpuUsage = 0,
    memoryUsage = 0,
    diskUsage = 0,
    networkRxMbps = 0,
    networkTxMbps = 0,
    uptimeHours = 0
  } = vmVitals || {};

  const isVmOff = uptimeHours === 0;

  // Formatter for uptime (e.g. 0.33 hours -> 20 mins or 2h 15m)
  const formatUptime = (hours) => {
    if (hours === 0 || !hours) return 'VM Off';
    const totalMinutes = Math.round(hours * 60);
    if (totalMinutes < 60) {
      return `${totalMinutes} mins`;
    }
    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return `${hrs}h ${mins}m`;
  };

  const getUsageColor = (val) => {
    if (isVmOff) return 'var(--text-muted)';
    if (val > 85) return 'var(--status-danger)';
    if (val > 70) return 'var(--status-warning)';
    return 'var(--primary-emerald)';
  };

  return (
    <div className="glass-card" style={{ marginBottom: '24px' }}>
      
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: isVmOff ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.15)',
            border: `1px solid ${isVmOff ? 'rgba(239, 68, 68, 0.3)' : 'rgba(16, 185, 129, 0.3)'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isVmOff ? 'var(--status-danger)' : 'var(--primary-emerald)'
          }}>
            {isVmOff ? <PowerOff size={22} /> : <Gauge size={22} />}
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#ffffff' }}>
                Virtual Machine Vitals
              </h2>
              {isVmOff && (
                <span className="badge-status unhealthy" style={{ fontSize: '0.7rem' }}>
                  <PowerOff size={12} /> VM Off
                </span>
              )}
            </div>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              Core compute hardware metrics & server health
            </p>
          </div>
        </div>

        {/* Uptime Pill Badge */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '6px', 
          background: isVmOff ? 'rgba(239, 68, 68, 0.12)' : 'rgba(16, 185, 129, 0.08)',
          border: `1px solid ${isVmOff ? 'rgba(239, 68, 68, 0.3)' : 'rgba(16, 185, 129, 0.2)'}`,
          padding: '6px 14px',
          borderRadius: '20px',
          fontSize: '0.8rem',
          color: isVmOff ? '#f87171' : 'var(--accent-mint)',
          fontWeight: 600
        }}>
          {isVmOff ? <PowerOff size={14} /> : <Clock size={14} />}
          <span>Status: {formatUptime(uptimeHours)}</span>
        </div>
      </div>

      {/* Grid of VM Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
        
        {/* CPU Usage Card */}
        <div style={{ background: 'rgba(6, 18, 14, 0.6)', padding: '16px', borderRadius: '12px', border: '1px solid rgba(16, 185, 129, 0.12)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Cpu size={16} color={isVmOff ? 'var(--text-muted)' : 'var(--primary-emerald)'} /> CPU Usage
            </span>
            <span style={{ fontSize: '1.2rem', fontWeight: 800, color: getUsageColor(cpuUsage) }}>
              {isVmOff ? 'OFF' : `${cpuUsage}%`}
            </span>
          </div>
          <MetricProgressBar percentage={isVmOff ? 0 : cpuUsage} color={getUsageColor(cpuUsage)} />
        </div>

        {/* RAM Memory Usage Card */}
        <div style={{ background: 'rgba(6, 18, 14, 0.6)', padding: '16px', borderRadius: '12px', border: '1px solid rgba(16, 185, 129, 0.12)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <HardDrive size={16} color={isVmOff ? 'var(--text-muted)' : 'var(--accent-mint)'} /> RAM Usage
            </span>
            <span style={{ fontSize: '1.2rem', fontWeight: 800, color: getUsageColor(memoryUsage) }}>
              {isVmOff ? 'OFF' : `${memoryUsage}%`}
            </span>
          </div>
          <MetricProgressBar percentage={isVmOff ? 0 : memoryUsage} color={getUsageColor(memoryUsage)} />
        </div>

        {/* Disk Usage Card */}
        <div style={{ background: 'rgba(6, 18, 14, 0.6)', padding: '16px', borderRadius: '12px', border: '1px solid rgba(16, 185, 129, 0.12)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Database size={16} color={isVmOff ? 'var(--text-muted)' : 'var(--accent-light)'} /> Disk Storage
            </span>
            <span style={{ fontSize: '1.2rem', fontWeight: 800, color: getUsageColor(diskUsage) }}>
              {isVmOff ? 'OFF' : `${diskUsage}%`}
            </span>
          </div>
          <MetricProgressBar percentage={isVmOff ? 0 : diskUsage} color={getUsageColor(diskUsage)} />
        </div>

        {/* Network I/O Bandwidth Card */}
        <div style={{ background: 'rgba(6, 18, 14, 0.6)', padding: '16px', borderRadius: '12px', border: '1px solid rgba(16, 185, 129, 0.12)' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
            <Wifi size={16} color={isVmOff ? 'var(--text-muted)' : 'var(--primary-emerald)'} /> Network I/O (Mbps)
          </span>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <ArrowDownLeft size={14} color={isVmOff ? 'var(--text-muted)' : 'var(--accent-mint)'} />
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Rx:</span>
              <strong style={{ fontSize: '1rem', color: isVmOff ? 'var(--text-muted)' : '#34d399' }}>{isVmOff ? '0' : networkRxMbps}</strong>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <ArrowUpRight size={14} color={isVmOff ? 'var(--text-muted)' : 'var(--accent-light)'} />
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Tx:</span>
              <strong style={{ fontSize: '1rem', color: isVmOff ? 'var(--text-muted)' : '#ffffff' }}>{isVmOff ? '0' : networkTxMbps}</strong>
            </div>
          </div>
        </div>

      </div>

    </div>
  );
};
